const ARTICLE_SLUGS = new Set([
  "ai-real-estate-search-india",
  "best-areas-to-buy-open-plots-near-hyderabad",
  "hmda-vs-dtcp-approved-layouts",
  "how-to-verify-plot-before-buying-hyderabad",
  "real-estate-project-listing-online-india",
  "rera-registered-projects-hyderabad",
  "what-is-lp-number-in-real-estate"
]);

/** Featured HTML linked a draft slug that is not in the folder yet. */
const SLUG_ALIASES = {
  "how-to-check-rera-registration-telangana": "rera-registered-projects-hyderabad"
};

const decodeHtml = (value = "") => value
  .replace(/&amp;/g, "&")
  .replace(/&lt;/g, "<")
  .replace(/&gt;/g, ">")
  .replace(/&quot;/g, '"')
  .replace(/&#39;/g, "'");

export const normalizeBlogSlug = (slug = "") => {
  const cleaned = String(slug).replace(/\.html$/i, "").replace(/^\/+/, "").trim();
  return SLUG_ALIASES[cleaned] || cleaned;
};

export const isKnownBlogSlug = (slug) => ARTICLE_SLUGS.has(normalizeBlogSlug(slug));

const scopeSelectors = (selectorBlock, scope) => selectorBlock.split(",").map((selector) => {
  const value = selector.trim();
  if (!value) {
    return value;
  }
  if (value === "*" || value === "body" || value === "html") {
    return scope;
  }
  if (/^(body|html)(\s|[.:#[>+~]|$)/.test(value)) {
    return `${scope}${value.replace(/^(body|html)/, "")}`;
  }
  if (
    value.startsWith(scope)
    || value.startsWith(":root")
    || value.startsWith(":focus")
    || value.startsWith(":hover")
    || /^\d/.test(value)
    || value === "from"
    || value === "to"
  ) {
    return value;
  }
  return `${scope} ${value}`;
}).join(", ");

/** Brace-aware CSS scoping so @media blocks keep working on tablet/mobile. */
const scopeCss = (css, scope = ".vd-blog") => {
  if (!css) {
    return "";
  }

  const processBlock = (block, insideKeyframes = false) => {
    let result = "";
    let i = 0;

    while (i < block.length) {
      if (block.startsWith("/*", i)) {
        const end = block.indexOf("*/", i + 2);
        result += block.slice(i, end === -1 ? block.length : end + 2);
        i = end === -1 ? block.length : end + 2;
        continue;
      }

      if (/\s/.test(block[i])) {
        result += block[i];
        i += 1;
        continue;
      }

      if (block[i] === "@") {
        const brace = block.indexOf("{", i);
        if (brace === -1) {
          result += block.slice(i);
          break;
        }

        const header = block.slice(i, brace).trim();
        let depth = 1;
        let j = brace + 1;
        while (j < block.length && depth > 0) {
          if (block[j] === "{") depth += 1;
          else if (block[j] === "}") depth -= 1;
          j += 1;
        }

        const inner = block.slice(brace + 1, j - 1);
        const isConditional = /^@(media|supports|layer)\b/i.test(header);
        const isKeyframes = /^@(keyframes|-webkit-keyframes)\b/i.test(header);

        result += `${header} {`;
        result += isConditional ? processBlock(inner, false) : isKeyframes ? processBlock(inner, true) : inner;
        result += "}";
        i = j;
        continue;
      }

      const brace = block.indexOf("{", i);
      if (brace === -1) {
        result += block.slice(i);
        break;
      }

      const selectors = block.slice(i, brace);
      let depth = 1;
      let j = brace + 1;
      while (j < block.length && depth > 0) {
        if (block[j] === "{") depth += 1;
        else if (block[j] === "}") depth -= 1;
        j += 1;
      }

      const decls = block.slice(brace + 1, j - 1);
      const scoped = insideKeyframes ? selectors.trim() : scopeSelectors(selectors, scope);
      result += `${scoped} {${decls.includes("{") ? processBlock(decls, insideKeyframes) : decls}}`;
      i = j;
    }

    return result;
  };

  return processBlock(css);
};

const rewriteInternalHref = (href = "") => {
  const raw = href.trim();
  if (!raw || raw.startsWith("#") || raw.startsWith("mailto:") || raw.startsWith("tel:") || /^https?:\/\//i.test(raw)) {
    return raw;
  }

  if (raw === "/" || raw === "/home" || raw === "/search") {
    return "/";
  }

  if (raw === "/blogs" || raw === "/blog" || raw === "blogs.html" || raw === "blog.html") {
    return "/blog";
  }

  if (raw === "/contact" || raw === "/about" || raw === "/how-it-works") {
    return raw;
  }

  const withoutPrefix = raw
    .replace(/^\/blogs?\//, "")
    .replace(/^blogs?\//, "")
    .replace(/^\.\//, "");
  const slug = normalizeBlogSlug(withoutPrefix);

  if (isKnownBlogSlug(slug)) {
    return `/blog/${slug}`;
  }

  return raw.startsWith("/") ? raw : `/blog/${slug}`;
};

export const rewriteBlogHtmlLinks = (html = "") => html.replace(
  /\shref=(["'])(.*?)\1/gi,
  (match, quote, href) => ` href=${quote}${rewriteInternalHref(href)}${quote}`
);

export const parseBlogHtml = (rawHtml = "") => {
  const title = decodeHtml(rawHtml.match(/<title[^>]*>([\s\S]*?)<\/title>/i)?.[1]?.trim() || "Blog");
  const description = decodeHtml(
    rawHtml.match(/<meta[^>]*name=["']description["'][^>]*content=["']([\s\S]*?)["'][^>]*>/i)?.[1]?.trim()
    || rawHtml.match(/<meta[^>]*content=["']([\s\S]*?)["'][^>]*name=["']description["'][^>]*>/i)?.[1]?.trim()
    || ""
  );

  const styles = [...rawHtml.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)]
    .map((match) => match[1].replace(/max-width:\s*820px/gi, "max-width: 1200px"))
    .join("\n");

  const jsonLd = [...rawHtml.matchAll(/<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi)]
    .map((match) => {
      try {
        return JSON.parse(match[1]);
      } catch {
        return null;
      }
    })
    .filter(Boolean);

  let body = rawHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i)?.[1] || "";
  body = body
    .replace(/<header[\s\S]*?<\/header>/gi, "")
    .replace(/<footer[\s\S]*?<\/footer>/gi, "")
    .replace(/<div class=["']header["'][\s\S]*?<\/div>\s*(?=<section|<main|<article)/gi, "");

  return {
    title: title.replace(/\s*\|\s*VenturesDirectoryAI.*$/i, "").trim() || title,
    description,
    styles: scopeCss(styles),
    bodyHtml: rewriteBlogHtmlLinks(body),
    jsonLd
  };
};
